<template>
  <v-container grid-list-xs>
    <!-- loading -->
    <div v-if="loading" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else-if="items.length > 0">
      <!-- seção GMR -->
      <v-card class="mb-4 elevation-1">
        <v-toolbar flat>
          <v-toolbar-title>GMR - Garantia Mínima de Resultado</v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-chip
            v-if="gmrInfo"
            x-small
            :color="gmrInfo.superou ? 'success' : 'grey'"
            dark
            class="mr-2"
          >
            {{ gmrInfo.superou ? 'Atingido' : 'Não Atingido' }}
          </v-chip>
          <span v-if="gmrInfo && gmrInfo.superou" class="caption primary--text font-weight-medium">
            {{ labelBonusGmr }}
          </span>
          <span v-if="!gmrInfo" class="caption grey--text">Sem vínculo GMR</span>
        </v-toolbar>

        <v-data-table
          v-if="gmrInfo"
          :headers="headersGmr"
          :items="gmrRows"
          hide-default-footer
          disable-pagination
          no-data-text="Sem dados GMR"
        >
          <template v-slot:[`item.indicador`]="{ item }">
            <BaseLineTableCodDescription
              :cod="item.id_indicador_gmr"
              :description="item.indicador_gmr || item.indicador"
            />
          </template>
          <template v-slot:[`item.valor_meta_gmr`]="{ item }">{{ item.valor_meta_gmr }}</template>
          <template v-slot:[`item.valor_realizado_gmr`]="{ item }">{{ item.valor_realizado_gmr }}</template>
          <template v-slot:[`item.crescimento_gmr`]="{ item }">{{ item.crescimento_gmr }}%</template>
          <template v-slot:[`item.percentual_gmr`]="{ item }">
            {{ item.percentual_gmr != null ? `${item.percentual_gmr}%` : '—' }}
          </template>
          <template v-slot:[`item.percentual_dobro`]="{ item }">
            {{ item.percentual_dobro != null ? `${item.percentual_dobro}%` : '—' }}
          </template>
        </v-data-table>

        <v-alert v-else dense text type="info" class="ma-3 mb-2">
          Esta campanha <strong>não tem vínculo com GMR</strong>. A trava de
          crescimento não se aplica, então o bônus apurado é pago integralmente
          a quem bate a meta.
        </v-alert>
      </v-card>

      <!-- toolbar com busca global -->
      <v-card class="mb-4">
        <v-toolbar flat>
          <v-toolbar-title>Apuração</v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-text-field
            v-model="search"
            class="mt-3"
            placeholder="Buscar colaborador"
            prepend-icon="mdi-magnify"
            clearable
          />
        </v-toolbar>
      </v-card>

      <!-- uma meta por componente de tipo -->
      <div style="display: flex; flex-direction: column; gap: 16px">
        <p v-if="metasAgrupadas.length === 0" class="body-2 grey--text text-center py-6 ma-0">
          Nenhum resultado disponível.
        </p>
        <component
          v-for="meta in metasAgrupadas"
          :key="meta.id_campanha_meta"
          :is="componentePorTipo(meta.id_tipo_conteudo)"
          :meta="meta"
          :search="search"
          :gmr-info="gmrInfo"
        />
      </div>
    </template>

    <p v-else class="body-2 grey--text text-center py-6 ma-0">
      Sem dados de apuração disponíveis.
    </p>
  </v-container>
</template>

<script>
import http from "@/services/http";
import dobrouGmr from "@/utils/dobrouGmr";
import BaseLineTableCodDescription from "@/components/shared/BaseLineTableCodDescription";
import ApuracaoIndicador from "./tipos/ApuracaoIndicador";
import ApuracaoChassi    from "./tipos/ApuracaoChassi";
import ApuracaoModelo    from "./tipos/ApuracaoModelo";
import ApuracaoPeca      from "./tipos/ApuracaoPeca";
import ApuracaoServico   from "./tipos/ApuracaoServico";

const TIPO_COMPONENTE = {
  1: "ApuracaoIndicador",
  2: "ApuracaoChassi",
  3: "ApuracaoModelo",
  4: "ApuracaoPeca",
  5: "ApuracaoServico"
};

export default {
  name: "SectionApuracao",

  components: {
    BaseLineTableCodDescription,
    ApuracaoIndicador,
    ApuracaoChassi,
    ApuracaoModelo,
    ApuracaoPeca,
    ApuracaoServico
  },

  props: {
    // Quando fornecido pelo DetalhesCampanha (já buscou os dados na sondagem),
    // evita uma segunda requisição ao mesmo endpoint.
    itemsApuracao: { type: Array, default: null }
  },

  data() {
    return {
      items:   [],
      loading: false,
      search:  ""
    };
  },

  computed: {
    // campanha vinculada ao GMR: prioriza a flag vincula_gmr; se a view ainda
    // não devolver a flag, cai pro fallback valor_meta_gmr > 0 (sem vínculo grava 0).
    campanhaTemGmr() {
      return this.items.find(
        r =>
          r.vincula_gmr === "S" ||
          r.vincula_gmr === true ||
          (r.vincula_gmr == null && parseFloat(r.valor_meta_gmr) > 0)
      );
    },

    gmrInfo() {
      const row = this.campanhaTemGmr;
      if (!row) return null;
      return {
        superou:          row.superou_gmr === "S" || row.superou_gmr === true,
        crescimento_gmr:  row.crescimento_gmr,
        percentual_gmr:   row.percentual_gmr,
        percentual_dobro: row.percentual_dobro
      };
    },

    // Dobro depende do crescimento apurado vs. percentual_dobro; percentual_gmr
    // e so o minimo que libera o bonus.
    labelBonusGmr() {
      const gmr = this.gmrInfo;
      return gmr && dobrouGmr(gmr.crescimento_gmr, gmr.percentual_dobro)
        ? "Bônus × 2"
        : "Bônus liberado";
    },

    gmrRows() {
      const row = this.campanhaTemGmr;
      if (!row) return [];
      return [{
        indicador:           row.indicador_gmr || row.indicador,
        id_indicador_gmr:    row.id_indicador_gmr,
        valor_meta_gmr:      row.valor_meta_gmr,
        valor_realizado_gmr: row.valor_realizado_gmr,
        crescimento_gmr:     row.crescimento_gmr,
        percentual_gmr:      row.percentual_gmr,
        percentual_dobro:    row.percentual_dobro
      }];
    },

    headersGmr() {
      return [
        { text: "Indicador",              value: "indicador",           sortable: false },
        { text: "Meta GMR",               value: "valor_meta_gmr",      align: "right", sortable: false },
        { text: "Realizado",              value: "valor_realizado_gmr", align: "right", sortable: false },
        { text: "% Crescimento",          value: "crescimento_gmr",     align: "right", sortable: false },
        { text: "% Crescimento Estimado", value: "percentual_gmr",      align: "right", sortable: false },
        { text: "% Crescimento Dobro",    value: "percentual_dobro",    align: "right", sortable: false }
      ];
    },

    metasAgrupadas() {
      const map = {};
      for (const row of this.items) {
        const id = row.id_campanha_meta;
        if (!id) continue;
        if (!map[id]) {
          map[id] = {
            id_campanha_meta:  id,
            id_tipo_conteudo:  row.id_tipo_conteudo,
            id_tipo_calculo:   row.id_tipo_calculo,
            id_tipo_pagamento: row.id_tipo_pagamento,
            tipo_conteudo:     row.tipo_conteudo,
            tipo_realizado:    row.tipo_realizado,
            tipo_calculo:      row.tipo_calculo,
            tipo_pagamento:    row.tipo_pagamento,
            superou_gmr:       false,
            labelIndicadores:  "",
            nParticipantes:    new Set(),
            rows:              []
          };
        }
        const m = map[id];
        m.rows.push(row);
        if (row.colaborador) m.nParticipantes.add(row.colaborador);
        if (row.superou_gmr === "S" || row.superou_gmr === true) m.superou_gmr = true;
        if (row.id_tipo_conteudo == 1 && row.indicador && !m.labelIndicadores.includes(row.indicador)) {
          m.labelIndicadores = m.labelIndicadores
            ? `${m.labelIndicadores}, ${row.indicador}`
            : row.indicador;
        }
      }
      return Object.values(map)
        .map(m => ({ ...m, nParticipantes: m.nParticipantes.size }))
        .sort(
          (a, b) =>
            Number(a.id_tipo_conteudo) - Number(b.id_tipo_conteudo) ||
            Number(a.id_campanha_meta) - Number(b.id_campanha_meta)
        );
    }
  },

  methods: {
    componentePorTipo(idTipoConteudo) {
      return TIPO_COMPONENTE[Number(idTipoConteudo)] || null;
    },

    async carregar() {
      this.loading = true;
      try {
        const { data } = await http.get("/campanhas/apuracao-campanha", {
          params: { id_campanha: this.$route.params.idCampanha, per_page: -1 }
        });
        this.items = Array.isArray(data) ? data : data.data || [];
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
      }
    }
  },

  mounted() {
    if (this.itemsApuracao) {
      this.items = this.itemsApuracao;
    } else {
      this.carregar();
    }
  }
};
</script>
